import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Phone, MessageSquareText } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { BUSINESS } from "@/lib/business";

/**
 * PUBLIC page for A2P 10DLC / Campaign Registry vetters.
 * Direct URL: /sms — no auth, no interaction required.
 *
 * Everything a carrier vetter looks for lives on this ONE screen:
 *   - Program (campaign) name + brand
 *   - How the consumer opts in (Call to Action)
 *   - Sample messages sent by the program
 *   - Message frequency + msg&data rates + STOP + HELP language
 *   - Non-preselected consent checkbox rendered in-place
 *   - Direct links to Privacy Policy and Terms of Service
 *   - Statement that consent is NOT a condition of purchase
 *   - Statement that opt-in data is NOT shared with third parties
 */
export default function SmsCampaign() {
  // Interactive demo checkbox — no submit; purely so the vetter can see the
  // exact widget the consumer sees.
  const [demoChecked, setDemoChecked] = useState(false);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <header className="max-w-3xl mx-auto px-6 md:px-10 pt-10 pb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs text-[#6B7280] hover:text-[#1E3B2F]"
          data-testid="sms-cta-back-link"
        >
          <ArrowLeft size={12} /> Back to Gentry
        </Link>
        <div className="font-label text-[10px] text-[#C25934] mt-6">
          A2P 10DLC · Campaign Details · Gens Realty
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1E3B2F] mt-3">
          SMS Opt-In · Call to Action
        </h1>
        <p className="text-sm text-[#6B7280] mt-3">
          This page documents exactly how visitors and callers opt in to receive
          text (SMS) messages from Gens Realty. It is intended for review by
          Twilio, The Campaign Registry (TCR), and US mobile carriers.
        </p>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 pb-24 space-y-6" data-testid="sms-cta-page">
        <Card title="Brand & Program">
          <Grid>
            <KV k="Legal business name" v="Gens Realty" />
            <KV k="Doing business as" v="Gens Realty" />
            <KV k="Website" v={typeof window !== "undefined" ? window.location.origin : ""} />
            <KV k="Business location" v="Houston, TX, USA" />
            <KV k="Business phone" v={BUSINESS.phone} />
            <KV k="Business email" v="hello@gensrealty.com" />
            <KV k="Campaign / program name" v="Gens Realty Customer Notifications" />
            <KV
              k="Use case"
              v="Customer care (real-estate brokerage): appointment confirmations, follow-ups on inquiries, occasional listing updates the customer asked about."
            />
            <KV k="Estimated message volume" v="Message frequency varies" />
          </Grid>
        </Card>

        <Card title="Where consumers opt in (Call to Action)">
          <p className="text-sm text-[#3d3d3d]">
            All opt-ins are collected at{" "}
            <a
              href="/"
              className="underline text-[#1E3B2F] hover:text-[#C25934]"
              data-testid="sms-cta-home-link"
            >
              our website
            </a>{" "}
            through one of two on-page dialogs — the &ldquo;Leave a message&rdquo; form
            and the &ldquo;Book an appointment&rdquo; form. Every visitor sees the
            checkbox below (in English or Kreyòl Ayisyen depending on the
            language they&apos;re using), <strong>unchecked</strong> by default.
            Submitting the form is only counted as an opt-in when the checkbox
            has been actively ticked.
          </p>

          <div className="mt-5">
            <div className="font-label text-[10px] text-[#6B7280] mb-2">
              Exact consumer-facing widget (interactive preview)
            </div>
            <div
              className="flex items-start gap-3 p-3 rounded-lg bg-[#F9F6F0] border border-[#EBE4D3]"
              data-testid="sms-cta-consent-widget"
            >
              <Checkbox
                id="cta-demo"
                checked={demoChecked}
                onCheckedChange={setDemoChecked}
                className="mt-0.5 border-[#1E3B2F] data-[state=checked]:bg-[#C25934] data-[state=checked]:border-[#C25934]"
                data-testid="sms-cta-demo-checkbox"
              />
              <label htmlFor="cta-demo" className="cursor-pointer select-none text-[#1F1F1F]">
                <div className="text-xs font-semibold leading-tight">
                  Yes, sign me up — text me updates about my request.
                </div>
                <div className="text-[11px] text-[#6B7280] mt-1.5 leading-snug">
                  By checking this box, I consent to receive SMS text messages
                  from Gens Realty at the phone number I provided. Message
                  types include appointment confirmations, follow-ups on my
                  inquiry, and occasional listing updates I requested. Message
                  frequency varies. Message and data rates may apply. Reply
                  STOP to unsubscribe, HELP for help. Consent is not a
                  condition of purchase.
                </div>
                <div className="text-[11px] text-[#6B7280] mt-1.5">
                  See our{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-[#1E3B2F] hover:text-[#C25934]"
                  >
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-[#1E3B2F] hover:text-[#C25934]"
                  >
                    Privacy Policy
                  </Link>
                  .
                </div>
              </label>
            </div>
          </div>

          <div className="mt-5 space-y-2">
            <div className="font-label text-[10px] text-[#6B7280]">Phone / IVR channel</div>
            <p className="text-sm text-[#3d3d3d]">
              Callers to <span className="font-medium">{BUSINESS.phone}</span>{" "}
              who leave a message or book an appointment are asked verbally:{" "}
              <em>
                &ldquo;Would you like us to text you appointment confirmations,
                follow-ups on your request, and occasional listing updates?
                Message frequency varies. Reply STOP any time to unsubscribe.
                Message and data rates may apply. Say YES to sign up, or NO if
                you&apos;d rather not.&rdquo;
              </em>{" "}
              Only an explicit &ldquo;yes&rdquo; is recorded as an opt-in; the
              verbatim transcript is stored with a timestamp and the Twilio
              CallSid as evidence.
            </p>
          </div>
        </Card>

        <Card title="Sample messages sent by this campaign">
          <SampleMsg
            label="Sample #1 — Lead follow-up (English)"
            body="Gens Realty: Hi Jane, thanks for reaching out — an agent will follow up with you shortly. Reply STOP to unsubscribe, HELP for help. Msg&Data rates may apply."
          />
          <SampleMsg
            label="Sample #2 — Appointment confirmation (English)"
            body="Gens Realty: Hi Jane, your appointment is confirmed for 2026-02-20 at 10:00. See you then! Reply STOP to unsubscribe, HELP for help. Msg&Data rates may apply."
          />
          <SampleMsg
            label="Sample #3 — 24-hour appointment reminder (English)"
            body="Gens Realty: Reminder — tomorrow you have an appointment at 10:00. Reply STOP to unsubscribe, HELP for help. Msg&Data rates may apply."
          />
          <SampleMsg
            label="Sample #4 — Appointment confirmation (Kreyòl Ayisyen)"
            body="Gens Realty: Bonjou Jane, randevou w konfime pou 2026-02-20 nan 10:00. Nou p ap tann ou. Reponn STOP pou anile, HELP pou èd. Frè mesaj/done ka aplike."
          />
          <SampleMsg
            label="Sample #5 — Response to caller replying HELP"
            body="Gens Realty: reply STOP to unsubscribe. For a person call (346) 699-2755. Msg&Data rates may apply."
          />
        </Card>

        <Card title="Required disclosures (all met)">
          <Checklist
            items={[
              "Business name (Gens Realty) is disclosed on the CTA and every message.",
              "Program purpose stated: appointment confirmations, follow-ups, occasional listing updates.",
              "Message frequency varies.",
              "Message and data rates may apply — stated in the CTA and every marketing message.",
              "STOP to opt out — supported on the inbound webhook and confirmed in the reply.",
              "HELP for help — supported on the inbound webhook and replies with our support number.",
              "Consent is not a condition of purchase — stated on the CTA.",
              "Opt-in data is not shared with any third party for marketing (see Privacy Policy §3).",
              "Consent checkbox is not pre-selected.",
              "Full audit trail per opt-in: timestamp, IP, user agent, verbatim disclosure text, campaign version.",
            ]}
          />
        </Card>

        <Card title="How to stop or get help">
          <p className="text-sm text-[#3d3d3d]">
            <strong>STOP:</strong> reply STOP (or STOPALL, UNSUBSCRIBE, CANCEL,
            END, QUIT, REVOKE) to any Gens Realty text and you will be
            unsubscribed immediately.
          </p>
          <p className="text-sm text-[#3d3d3d] mt-2">
            <strong>HELP:</strong> reply HELP (or INFO) for support instructions.
          </p>
          <p className="text-sm text-[#3d3d3d] mt-2">
            <strong>Re-subscribe:</strong> reply START at any time.
          </p>
          <p className="text-sm text-[#3d3d3d] mt-2">
            <strong>Contact:</strong> call{" "}
            <a className="underline" href={`tel:${BUSINESS.phoneTel}`}>
              {BUSINESS.phone}
            </a>{" "}
            or email{" "}
            <a className="underline" href="mailto:hello@gensrealty.com">
              hello@gensrealty.com
            </a>
            .
          </p>
        </Card>

        <div className="pt-2 flex flex-wrap gap-3 text-sm">
          <Link
            to="/privacy"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#EBE4D3] hover:border-[#1E3B2F] text-[#1E3B2F]"
            data-testid="sms-cta-privacy-link"
          >
            <MessageSquareText size={14} /> Privacy Policy
          </Link>
          <Link
            to="/terms"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#EBE4D3] hover:border-[#1E3B2F] text-[#1E3B2F]"
            data-testid="sms-cta-terms-link"
          >
            <MessageSquareText size={14} /> Terms of Service
          </Link>
          <a
            href={`tel:${BUSINESS.phoneTel}`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#C25934] hover:bg-[#a44827] text-white"
          >
            <Phone size={14} /> Call {BUSINESS.phone}
          </a>
        </div>
      </main>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="bg-white rounded-2xl border border-[#EBE4D3] p-6 md:p-8 shadow-sm">
      <h2 className="font-serif-display text-2xl text-[#1E3B2F] mb-4">{title}</h2>
      <div className="text-sm text-[#3d3d3d] space-y-3">{children}</div>
    </section>
  );
}

function Grid({ children }) {
  return <dl className="grid sm:grid-cols-2 gap-4">{children}</dl>;
}

function KV({ k, v }) {
  return (
    <div>
      <dt className="font-label text-[10px] text-[#6B7280]">{k}</dt>
      <dd className="text-sm text-[#1F1F1F] mt-1 break-words">{v}</dd>
    </div>
  );
}

function SampleMsg({ label, body }) {
  return (
    <div>
      <div className="font-label text-[10px] text-[#6B7280] mb-1">{label}</div>
      <div className="text-sm bg-[#F9F6F0] border-l-3 border-[#C25934] px-3 py-2 rounded whitespace-pre-wrap">
        {body}
      </div>
    </div>
  );
}

function Checklist({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2 text-sm text-[#1F1F1F]">
          <span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0 mt-0.5">
            <Check size={11} />
          </span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}
